import React, { Component } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, number, oneOfType } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { withViewport } from '../../util/contextHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { findOptionsForSelectFilter, findConfigOptionForSelectFilter } from '../../util/search';
import { formatMoney } from '../../util/currency';
import { createResourceLocatorString } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  findUserShowRatingByListingId,
  getUserShowRatings,
  userDisplayNameAsString,
} from '../../util/data';
import { prepareSocialSharingListingImages } from '../../util/seo';
import { richText } from '../../util/richText';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { joinWaitlist, leaveWaitlist } from './ListingPage.duck';
import {
  rateShow,
  addReviewDislike,
  addReviewLike,
  sendReview,
} from '../ReviewsPage/ReviewsPage.duck';
import {
  Page,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  ShowPanel,
  ShareModal,
  ShowRatingModal,
  PointsModal,
  SectionRecentlyViewedListings,
} from '../../components';
import { TopbarContainer, NotFoundPage } from '../../containers';
import getCountryCodes from '../../translations/countryCodes';
import moment from 'moment';

// Sections
import SectionImages from './SectionImages';
import SectionGalleryMaybe from './SectionGalleryMaybe';
import SectionLiveRoomsMaybe from './SectionLiveRoomsMaybe';
import SectionDescriptionMaybe from './SectionDescriptionMaybe';
import SectionDetails from './SectionDetails';
import ProductionStageArc, {
  PRODUCTION_STAGES,
} from '../../components/ProductionStageArc/ProductionStageArc';
import SectionReviews from './SectionReviews';
import SectionHostsMaybe from './SectionHostsMaybe';
import SectionHostMaybe from './SectionHostMaybe';
import SectionSimilarShowsMaybe from './SectionSimilarShowsMaybe';
// Modals
import SuccessPostingModal from './SuccessPostingModal';
import SuccessInviteModal from './SuccessInviteModal';
import DescriptionModal from './DescriptionModal';
import TourSchedulesModal from './TourSchedulesModal';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const MAX_MOBILE_SCREEN_WIDTH = 768;
const MAX_TABLET_SCREEN_WIDTH = 1024;

const { UUID, Money } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price.amount && price.currency) {
    const formattedPrice = formatMoney(intl, new Money(price.amount, price.currency));
    return { formattedPrice, priceTitle: formattedPrice };
  }

  return {};
};

export class ListingPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageClassNames: [],
      imageCarouselOpen: false,
      activeImageIndex: 0,
      successPostingModalOpen: false,
      successPostingListingState: null,
      descriptionModalOpen: false,
      successInviteModalOpen: false,
      shareModalOpen: false,
      showRatingModalOpen: false,
      pointsModalOpen: false,
      pointsForModal: 0,
      tourSchedulesModalOpen: false,
    };

    this.getCurrentListing = this.getCurrentListing.bind(this);
    this.handleRateShow = this.handleRateShow.bind(this);
  }

  getCurrentListing(listingId, isVariant) {
    const { getListing, getOwnListing } = this.props;

    let currentListing;

    if (isVariant) {
      const ownListing = ensureOwnListing(getOwnListing(listingId));
      currentListing = ownListing?.id ? ownListing : ensureListing(getListing(listingId));
    } else {
      currentListing = ensureListing(getListing(listingId));
    }

    return currentListing;
  }

  handleRateShow(values) {
    const { reviewRating } = values;
    const { params, onRateShow } = this.props;

    const listingId = params.id;

    // On a failure the modal stays open: the form shows the error the duck kept
    return onRateShow(reviewRating, listingId).then(
      () => {
        this.setState({
          showRatingModalOpen: false,
        });
      },
      () => {}
    );
  }

  componentDidMount() {
    const finishedListingCreation = this.props.location?.state?.finishedListingCreation;
    const listingState = this.props.location?.state?.listingState;
    const redirectedAfterInvite = this.props.location?.state?.redirectedAfterInvite;
    if (finishedListingCreation) {
      this.setState({
        successPostingModalOpen: !!finishedListingCreation,
        successPostingListingState: listingState,
      });
    } else if (redirectedAfterInvite) {
      this.setState({
        successInviteModalOpen: !!redirectedAfterInvite,
      });
    }
  }

  render() {
    const {
      intl,
      params: rawParams,
      history,
      location,
      filterConfig,
      // UI
      scrollingDisabled,
      onManageDisableScrolling,
      viewport,
      // Listing
      showListingInProgress,
      showListingError,
      // user(s)
      currentUser,
      currentUserReactions,
      users,
      fetchUsersInProgress,
      fetchUsersError,
      // reviews
      reviews,
      averageReview,
      featuredReviewId,
      onSendReview,
      sendReviewInProgress,
      sendReviewError,
      onAddReviewLike,
      addReviewLikeInProgress,
      addReviewLikeError,
      onAddReviewDislike,
      addReviewDislikeInProgress,
      addReviewDislikeError,
      // show(s)
      similarShows,
      querySimilarShowsInProgress,
      querySimilarShowsError,
      recentlyViewedListings,
      queryRecentlyViewedListingsInProgress,
      queryRecentlyViewedListingsError,
      rateShowInProgress,
      rateShowError,
      // experience(s)
      liveRooms,
      queryShowLiveRoomsInProgress,
      queryShowLiveRoomsError,
      // organizations
      organizationProfile,
      organizationProfiles,
      // waitlist
      joinWaitlistInProgress,
      joinWaitlistError,
      onJoinWaitlist,
      leaveWaitlistInProgress,
      leaveWaitlistError,
      onLeaveWaitlist,
      // show videos
    } = this.props;

    const listingId = new UUID(rawParams.id);
    const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
    const isVariant = isPendingApprovalVariant || isDraftVariant;
    const currentListing = this.getCurrentListing(listingId, isVariant);

    const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
    const params = { slug: listingSlug, ...rawParams };
    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
    const isTabletLayout = viewport.width < MAX_TABLET_SCREEN_WIDTH;

    const listingType = isDraftVariant
      ? LISTING_PAGE_PARAM_TYPE_DRAFT
      : LISTING_PAGE_PARAM_TYPE_EDIT;
    const listingTab = isDraftVariant ? 'touring-links' : 'basic';

    const isApproved =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

    const pendingIsApproved = isPendingApprovalVariant && isApproved;

    // If a /pending-approval URL is shared, the UI requires
    // authentication and attempts to fetch the listing from own
    // listings. This will fail with 403 Forbidden if the author is
    // another user. We use this information to try to fetch the
    // public listing.
    const pendingOtherUsersListing =
      (isPendingApprovalVariant || isDraftVariant) &&
      showListingError &&
      showListingError.status === 403;
    const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

    if (shouldShowPublicListingPage) {
      return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
    }

    const { description = '', price = null, title = '', publicData } = currentListing.attributes;

    // filter options from marketplace-custom-config.js
    const categoryOptions = findOptionsForSelectFilter('category', filterConfig);
    const lengthOptions = findOptionsForSelectFilter('length', filterConfig);
    const ageOptions = findOptionsForSelectFilter('age', filterConfig);
    const statusOptions = findOptionsForSelectFilter('status', filterConfig);
    const genreOptions = findOptionsForSelectFilter('genre', filterConfig);
    const supportedCountriesOptions = getCountryCodes('en').map(c => ({
      key: c.code,
      label: c.name,
    }));

    // find option from array of options
    const categoryConfig = findConfigOptionForSelectFilter(categoryOptions, publicData?.category);
    const lengthConfig = findConfigOptionForSelectFilter(lengthOptions, publicData?.length);
    const ageConfig = findConfigOptionForSelectFilter(ageOptions, publicData?.age);
    const statusConfig = findConfigOptionForSelectFilter(statusOptions, publicData?.status);
    const genreConfig = findConfigOptionForSelectFilter(genreOptions, publicData?.genre);
    const countryOfOriginConfig = findConfigOptionForSelectFilter(
      supportedCountriesOptions,
      publicData?.countryOfOrigin
    );

    // option label & publicData value
    const category = categoryConfig?.label;
    const length = lengthConfig?.label;
    const age = ageConfig?.label;
    const status = statusConfig?.label;
    const touringLink = publicData?.touringLink;
    const address = publicData?.location?.address;
    const coverImageId = publicData?.coverImageId;
    const posterImageId = publicData?.posterImageId;
    const genre = genreConfig?.label;
    const launch = publicData?.launch;
    const website = publicData?.website;
    const countryOfOrigin = countryOfOriginConfig?.label;

    // dates
    const { start, end } = publicData?.dates || {};

    const startDate = moment(start).format('MMM DD');
    const endDate = moment(end).format('MMM DD');

    const endDateYear = moment(end).year();
    const showDate = `${startDate} - ${endDate}, ${endDateYear}`;

    // listing fields object
    const listingFields = {
      description,
      category,
      length,
      age,
      status,
      address,
      touringLink,
      showDate,
      genre,
      website,
      launch,
      countryOfOrigin,
    };

    const videos = publicData?.videos || [];
    const hasVideos = videos.length > 0;

    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const topbar = (
      <TopbarContainer desktopClassName={css.topbarDesktop} contentClassName={css.topbarContent} />
    );

    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);

    const currentUserShowRatings = getUserShowRatings(currentUser);
    const currentUserShowRating = findUserShowRatingByListingId(
      currentUserShowRatings,
      listingId.uuid
    );

    if (showListingError && showListingError.status === 404) {
      // 404 listing not found

      return <NotFoundPage />;
    } else if (showListingError) {
      // Other error in fetching listing

      const errorTitle = intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      });

      return (
        <Page title={errorTitle} scrollingDisabled={scrollingDisabled} currentPage="ListingPage">
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.errorText}>
                <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer currentPage="ListingPage" />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    } else if (!currentListing.id) {
      // Still loading the listing

      const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      });

      return (
        <Page title={loadingTitle} scrollingDisabled={scrollingDisabled} currentPage="ListingPage">
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.loadingText}>
                <FormattedMessage id="ListingPage.loadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer currentPage="ListingPage" />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    }

    const handleViewPhotosClick = (e, imageIndex = 0) => {
      // Stop event from bubbling up to prevent image click handler
      // trying to open the carousel as well.
      e.stopPropagation();
      this.setState({
        imageCarouselOpen: true,
        activeImageIndex: imageIndex,
      });
    };

    // When user is banned or deleted the listing is also deleted.
    // Because listing can be never showed with banned or deleted user we don't have to provide
    // banned or deleted display names for the function
    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

    const { formattedPrice } = priceData(price, intl);

    const listingImages = (listing, variantName) =>
      (listing.images || [])
        .map(image => {
          const variants = image.attributes.variants;
          const variant = variants ? variants[variantName] : null;

          // deprecated
          // for backwards combatility only
          const sizes = image.attributes.sizes;
          const size = sizes ? sizes.find(i => i.name === variantName) : null;

          return variant || size;
        })
        .filter(variant => variant != null);

    const sharingImages = listingImages(currentListing, 'portrait-crop2x');
    const sharingImageUrls = sharingImages.map(img => img.url);

    const siteTitle = config.siteTitle;
    const schemaTitle = intl.formatMessage(
      { id: 'ListingPage.schemaTitle' },
      { title, price: formattedPrice, siteTitle }
    );

    // Dynamic SEO description
    const dynamicDescription = description
      ? `${description.substring(0, 150)}${
          description.length > 150 ? '...' : ''
        } Show by ${authorDisplayName}.`
      : intl.formatMessage({ id: 'ListingPage.schemaDescription' });

    // Dynamic keywords based on content
    const baseKeywords = intl.formatMessage({ id: 'ListingPage.schemaKeywords' });
    const categoryKeywords = publicData?.category ? `, ${publicData.category}` : '';
    const genreKeywords = publicData?.genre ? `, ${publicData.genre}` : '';
    const locationKeywords = publicData?.location?.address
      ? `, ${publicData.location.address}`
      : '';
    const dynamicKeywords = `${baseKeywords}${categoryKeywords}${genreKeywords}${locationKeywords}`;

    const editParams = {
      id: listingId.uuid,
      slug: listingSlug,
      type: listingType,
      tab: listingTab,
    };
    const editMessageId = isDraftVariant ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        currentPage="ListingPage"
        contentType="website"
        description={dynamicDescription}
        keywords={dynamicKeywords}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'ItemPage',
          description: dynamicDescription,
          name: schemaTitle,
        }}
        socialSharing={{
          images1200: prepareSocialSharingListingImages(sharingImageUrls, {
            width: 280,
            height: 392,
          }),
        }}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar className={css.pageTopbar}>{topbar}</LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <div>
              <SectionImages
                title={title}
                richTitle={richTitle}
                listing={currentListing}
                location={location}
                showListingInProgress={showListingInProgress}
                currentUser={currentUser}
                currentAuthor={currentAuthor}
                authorDisplayName={authorDisplayName}
                reviews={reviews}
                averageReview={averageReview}
                coverImageId={coverImageId}
                posterImageId={posterImageId}
                organizationProfile={organizationProfile}
                organizationProfiles={organizationProfiles}
                isVariant={isVariant}
                isOwnListing={isOwnListing}
                isMobileLayout={isMobileLayout}
                isTabletLayout={isTabletLayout}
                imageCarouselOpen={this.state.imageCarouselOpen}
                activeImageIndex={this.state.activeImageIndex}
                editParams={editParams}
                editMessageId={editMessageId}
                handleViewPhotosClick={handleViewPhotosClick}
                onImageCarouselClose={() => this.setState({ imageCarouselOpen: false })}
                onOpenShareModal={() => this.setState({ shareModalOpen: true })}
                onOpenDescriptionModal={() => this.setState({ descriptionModalOpen: true })}
                onOpenTourSchedulesModal={() => this.setState({ tourSchedulesModalOpen: true })}
                onOpenShowRatingModal={() => {
                  if (currentUser) {
                    this.setState({
                      showRatingModalOpen: true,
                    });
                  } else {
                    const state = {
                      from: `${location.pathname}${location.search}${location.hash}`,
                    };

                    const routes = routeConfiguration();
                    history.push(createResourceLocatorString('LoginPage', routes), state);
                  }
                }}
                onManageDisableScrolling={onManageDisableScrolling}
              />
              <div className={css.contentContainer}>
                <div className={css.contentWrapper}>
                  <div className={css.mainContent}>
                    <SectionDescriptionMaybe title={title} description={description} />
                    <SectionGalleryMaybe
                      intl={intl}
                      images={currentListing.images || []}
                      videos={videos}
                      isMobileLayout={isMobileLayout}
                      imageVariants={[
                        'scaled-small',
                        'scaled-medium',
                        'scaled-large',
                        'scaled-xlarge',
                      ]}
                      onManageDisableScrolling={onManageDisableScrolling}
                      handleViewPhotosClick={handleViewPhotosClick}
                    />
                    <SectionHostsMaybe
                      listing={currentListing}
                      users={users}
                      fetchUsersInProgress={fetchUsersInProgress}
                      fetchUsersError={fetchUsersError}
                      teamFromPublicData={publicData?.team}
                    />
                    <SectionLiveRoomsMaybe
                      intl={intl}
                      liveRooms={liveRooms}
                      queryShowLiveRoomsInProgress={queryShowLiveRoomsInProgress}
                      queryShowLiveRoomsError={queryShowLiveRoomsError}
                    />
                    <SectionReviews
                      intl={intl}
                      location={location}
                      history={history}
                      listingId={listingId}
                      reviews={reviews}
                      featuredReviewId={featuredReviewId}
                      currentUser={currentUser}
                      currentUserReactions={currentUserReactions}
                      listingTitle={title}
                      isOwnListing={isOwnListing}
                      isMobileLayout={isMobileLayout}
                      sendReviewInProgress={sendReviewInProgress}
                      sendReviewError={sendReviewError}
                      addReviewLikeInProgress={addReviewLikeInProgress}
                      addReviewLikeError={addReviewLikeError}
                      addReviewDislikeInProgress={addReviewDislikeInProgress}
                      addReviewDislikeError={addReviewDislikeError}
                      onSendReview={review => onSendReview(listingId, review)}
                      onAddReviewLike={reviewId => onAddReviewLike(listingId, reviewId)}
                      onAddReviewDislike={reviewId => onAddReviewDislike(listingId, reviewId)}
                      onOpenPointsModal={points =>
                        this.setState({
                          pointsModalOpen: true,
                          pointsForModal: points,
                        })
                      }
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                    {PRODUCTION_STAGES.some(s => s.key === publicData?.status) && (
                      <div className={css.productionStageSection}>
                        <ProductionStageArc value={publicData.status} />
                      </div>
                    )}
                    <SectionDetails
                      listingFields={listingFields}
                      organizationProfile={organizationProfile}
                    />
                    <SectionHostMaybe
                      listing={currentListing}
                      organizationProfile={organizationProfile}
                    />
                  </div>
                  <ShowPanel
                    className={css.showPanel}
                    listing={currentListing}
                    showListingInProgress={showListingInProgress}
                    listingFields={listingFields}
                    isOwnListing={isOwnListing}
                    onOpenDescriptionModal={() => this.setState({ descriptionModalOpen: true })}
                    onJoinWaitlist={onJoinWaitlist}
                    joinWaitlistInProgress={joinWaitlistInProgress}
                    joinWaitlistError={joinWaitlistError}
                    onLeaveWaitlist={onLeaveWaitlist}
                    leaveWaitlistInProgress={leaveWaitlistInProgress}
                    leaveWaitlistError={leaveWaitlistError}
                    currentUser={currentUser}
                    onOpenTourSchedulesModal={() => this.setState({ tourSchedulesModalOpen: true })}
                  />
                </div>

                <SectionSimilarShowsMaybe
                  intl={intl}
                  listingId={listingId}
                  similarShows={similarShows}
                  querySimilarShowsInProgress={querySimilarShowsInProgress}
                  querySimilarShowsError={querySimilarShowsError}
                />
              </div>
            </div>
            <ShareModal
              id="ListingPage.shareModal"
              location={location}
              isOpen={this.state.shareModalOpen}
              onCloseModal={() => this.setState({ shareModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
              titleMessage={intl.formatMessage(
                {
                  id: 'ListingPage.shareModalTitle',
                },
                { listingTitle: title }
              )}
            />
            <SuccessPostingModal
              id="ListingPage.successPostingModal"
              containerClassName={css.descriptionModalContainer}
              contentClassName={css.modalContent}
              listingId={listingId}
              orgId={currentListing?.attributes?.metadata?.organizationId || null}
              location={location}
              listingState={this.state.successPostingListingState}
              isOpen={this.state.successPostingModalOpen}
              onCloseModal={() => {
                this.setState({
                  successPostingModalOpen: false,
                  successPostingListingState: null,
                });

                // clear location state
                history.replace();
              }}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            <SuccessInviteModal
              id="ListingPage.successInviteModal"
              containerClassName={css.descriptionModalContainer}
              contentClassName={css.modalContent}
              currentUser={currentUser}
              isOpen={this.state.successInviteModalOpen}
              onCloseModal={() => {
                this.setState({ successInviteModalOpen: false });

                // clear location state
                history.replace();
              }}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            <DescriptionModal
              id="ListingPage.descriptionModal"
              containerClassName={css.modalContainer}
              contentClassName={css.modalContent}
              isOpen={this.state.descriptionModalOpen}
              listingTitle={title}
              description={description}
              onCloseModal={() => this.setState({ descriptionModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            <TourSchedulesModal
              id="ListingPage.tourSchedulesModal"
              containerClassName={css.modalContainer}
              contentClassName={css.modalContent}
              isOpen={this.state.tourSchedulesModalOpen}
              onCloseModal={() => this.setState({ tourSchedulesModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
              tourSchedules={publicData?.tourSchedules || []}
              listingTitle={title}
            />
            <ShowRatingModal
              id="ListingPage.showRatingModal"
              containerClassName={css.modalContainer}
              contentClassName={css.modalContent}
              isOpen={this.state.showRatingModalOpen}
              onCloseModal={() => this.setState({ showRatingModalOpen: false })}
              onSubmitRating={this.handleRateShow}
              reviewee={title}
              sendRatingInProgress={rateShowInProgress}
              sendRatingError={rateShowError}
              showRating={currentUserShowRating}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            <PointsModal
              id="ListingPage.pointsModal"
              isOpen={this.state.pointsModalOpen}
              currentUser={currentUser}
              points={this.state.pointsForModal}
              onCloseModal={() =>
                this.setState({
                  pointsModalOpen: false,
                  pointsForModal: 0,
                })
              }
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            {!isVariant && (
              <SectionRecentlyViewedListings
                intl={intl}
                recentlyViewedListings={recentlyViewedListings}
                queryRecentlyViewedListingsInProgress={queryRecentlyViewedListingsInProgress}
                queryRecentlyViewedListingsError={queryRecentlyViewedListingsError}
              />
            )}
            <Footer currentPage="ListingPage" />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ListingPageComponent.defaultProps = {
  // listing
  showListingInProgress: false,
  showListingError: null,

  // user(s)
  isAuthenticated: false,
  currentUser: null,
  users: [],
  fetchUsersInProgress: false,
  fetchUsersError: null,
  hasUsers: false,

  // reviews
  sendReviewInProgress: false,
  sendReviewError: null,
  addReviewLikeInProgress: false,
  addReviewLikeError: null,
  addReviewDislikeInProgress: false,
  addReviewDislikeError: null,

  // show(s)
  similarShows: [],
  querySimilarShowsInProgress: false,
  queryShowLiveRoomsError: null,
  recentlyViewedListings: [],
  queryRecentlyViewedListingsInProgress: false,
  queryRecentlyViewedListingsError: null,
  rateShowInProgress: false,
  rateShowError: null,
  onRateShow: null,

  // experience(s)
  hasShowExperience: false,
  liveRooms: [],
  queryShowLiveRoomsInProgress: false,
  queryShowLiveRoomsError: null,

  // organizations
  organizationProfile: null,

  // waitlist
  joinWaitlistInProgress: false,
  joinWaitlistError: null,
  onJoinWaitlist: null,
  leaveWaitlistInProgress: false,
  leaveWaitlistError: null,
  onLeaveWaitlist: null,

  filterConfig: config.custom.filters,
};

ListingPageComponent.propTypes = {
  // UI
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,

  // listing
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  showListingError: propTypes.error,

  // reviews
  reviews: array.isRequired,
  averageReview: oneOfType([number, string]).isRequired,
  onSendReview: func.isRequired,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  onAddReviewLike: func.isRequired,
  addReviewLikeInProgress: bool.isRequired,
  addReviewLikeError: propTypes.error,
  onAddReviewDislike: func.isRequired,
  addReviewDislikeInProgress: bool.isRequired,
  addReviewDislikeError: propTypes.error,

  // user(s)
  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  users: arrayOf(propTypes.user),
  fetchUsersInProgress: bool.isRequired,
  fetchUsersError: propTypes.error,
  hasUsers: bool.isRequired,

  // show(s)
  similarShows: arrayOf(propTypes.listing),
  querySimilarShowsInProgress: bool.isRequired,
  queryShowLiveRoomsError: propTypes.error,
  recentlyViewedListings: arrayOf(propTypes.listing),
  queryRecentlyViewedListingsInProgress: bool.isRequired,
  queryRecentlyViewedListingsError: propTypes.error,
  rateShowInProgress: bool.isRequired,
  rateShowError: propTypes.error,
  onRateShow: func.isRequired,

  // experience(s)
  hasShowExperience: bool.isRequired,
  liveRooms: arrayOf(propTypes.listing),
  queryShowLiveRoomsInProgress: bool.isRequired,
  queryShowLiveRoomsError: propTypes.error,
  author: propTypes.user,

  // organizations
  organizationProfile: oneOfType([propTypes.ownOrganization, propTypes.organization]),

  // waitlist
  joinWaitlistInProgress: bool.isRequired,
  joinWaitlistError: propTypes.error,
  onJoinWaitlist: func.isRequired,
  leaveWaitlistInProgress: bool.isRequired,
  leaveWaitlistError: propTypes.error,
  onLeaveWaitlist: func.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,
  filterConfig: array,

  // for CheckoutPage
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const {
    showListingError,
    showListingInProgress,
    userIds,
    fetchUsersInProgress,
    fetchUsersError,
    hasShowExperience,
    showLiveRoomsIds,
    queryShowLiveRoomsInProgress,
    queryShowLiveRoomsError,
    similarShowIds,
    querySimilarShowsInProgress,
    querySimilarShowsError,
    joinWaitlistInProgress,
    joinWaitlistError,
    leaveWaitlistInProgress,
    leaveWaitlistError,
  } = state.ListingPage;
  const {
    reviews,
    averageReview,
    featuredReviewId,
    fetchReviewsInProgress,
    fetchReviewsError,
    sendReviewInProgress,
    sendReviewError,
    addReviewLikeInProgress,
    addReviewLikeError,
    addReviewDislikeInProgress,
    addReviewDislikeError,
    rateShowInProgress,
    rateShowError,
  } = state.ReviewsPage;
  const {
    currentUser,
    currentUserReactions,
    recentlyViewedListingIds,
    queryRecentlyViewedListingsInProgress,
    queryRecentlyViewedListingsError,
  } = state.user;
  const { organizationProfile } = state.OrganizationProfilePage;
  const { organizationProfiles } = state.organizations;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const userEntities = userIds.map(userId => ({ type: 'user', id: userId }));
  const users = getMarketplaceEntities(state, userEntities);
  const hasUsers = users.length > 0;

  return {
    isAuthenticated,
    currentUser,
    currentUserReactions,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    showListingInProgress,
    showListingError,
    users,
    fetchUsersInProgress,
    fetchUsersError,
    hasUsers,
    hasShowExperience,
    liveRooms: showLiveRoomsIds.map(id => getListing(id)),
    queryShowLiveRoomsInProgress,
    queryShowLiveRoomsError,
    similarShows: similarShowIds.map(id => getListing(id)),
    querySimilarShowsInProgress,
    querySimilarShowsError,
    joinWaitlistInProgress,
    joinWaitlistError,
    leaveWaitlistInProgress,
    leaveWaitlistError,
    recentlyViewedListings: recentlyViewedListingIds.map(id => getListing(id)),
    queryRecentlyViewedListingsInProgress,
    queryRecentlyViewedListingsError,
    reviews,
    averageReview,
    featuredReviewId,
    fetchReviewsInProgress,
    fetchReviewsError,
    sendReviewInProgress,
    sendReviewError,
    addReviewLikeInProgress,
    addReviewLikeError,
    addReviewDislikeInProgress,
    addReviewDislikeError,
    organizationProfile,
    organizationProfiles,
    rateShowInProgress,
    rateShowError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onSendReview: (listingId, review) => dispatch(sendReview(listingId, review)),
  onAddReviewLike: (listingId, reviewId) => dispatch(addReviewLike(listingId, reviewId)),
  onAddReviewDislike: (listingId, reviewId) => dispatch(addReviewDislike(listingId, reviewId)),
  onRateShow: (rating, listingId) => dispatch(rateShow(rating, listingId)),
  onJoinWaitlist: listingId => dispatch(joinWaitlist(listingId)),
  onLeaveWaitlist: listingId => dispatch(leaveWaitlist(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withRouter,
  withViewport,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ListingPageComponent);

export default ListingPage;
